/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Papa from 'papaparse';

/* Import types */
import type {
    DeviceSensorMetadataEntry,
    DeviceSensorCsvMetadataGroup,
    DeviceSensorMetadataMap,
} from '@/types/deviceSensor';
import type { SensorCsvRow } from '@/types/sensorCsv';

export function getDeviceSensorCsvMetadataGroup(value?: string): DeviceSensorCsvMetadataGroup | null {
    switch (value?.trim()) {
        case 'pv':
            return 'PV';
        case 'bl':
        case 'bl_night':
            return 'BL';
        case 'heating_energy':
            return 'HE';
        case 'water_bl':
            return 'WATER';
        case 'gas_bl':
            return 'GAS';
        case 'grid_feed':
            return 'GRID_FEED';
        default:
            return null;
    }
}

function stripPowerSuffix(sensorName: string): string {
    return sensorName.endsWith('.P') ? sensorName.slice(0, -2) : sensorName;
}

function toOptionalText(value?: string): string | null {
    const trimmedValue = value?.trim();

    return trimmedValue ? trimmedValue : null;
}

function createEmptyMetadataEntry(): DeviceSensorMetadataEntry {
    return {
        anomaly: {},
        description: null,
        enabled: {},
        medium: {},
        system: null,
    };
}

export function csvToDeviceSensorMetadata(csv: string): DeviceSensorMetadataMap {
    const parsedCsv = Papa.parse<SensorCsvRow>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
    });

    return parsedCsv.data.reduce<DeviceSensorMetadataMap>((accumulator, row) => {
        const sensorName = toOptionalText(row.Sensor);

        if (!sensorName) {
            return accumulator;
        }

        const deviceName = stripPowerSuffix(sensorName);
        const mappedGroup = getDeviceSensorCsvMetadataGroup(row.Anomaly);
        const description = toOptionalText(row.Description);
        const medium = toOptionalText(row.Medium);
        const system = toOptionalText(row.System);
        const enabled = row.Enabled?.trim() === '1';
        const existingEntry = accumulator[deviceName] ?? createEmptyMetadataEntry();

        if (!existingEntry.description && description) {
            existingEntry.description = description;
        }

        if (!existingEntry.system && system) {
            existingEntry.system = system;
        }

        if (mappedGroup) {
            existingEntry.enabled[mappedGroup] = enabled;
            existingEntry.anomaly[mappedGroup] = mappedGroup;
            existingEntry.medium[mappedGroup] = medium;
        }

        accumulator[deviceName] = existingEntry;

        return accumulator;
    }, {});
}
